import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/EnterpriseAuthContext';
import { useApp } from '../../context/AppContext';
import { useAgentTools } from '../../assistant/agentTools';
import ChatAgentDock from '../ChatAgentDock';
import { parseDocumentEditorRoute } from '../../utils/navigation';

/**
 * Find a generated document and its project.
 * @param {Array<object>} projects - AppContext projects.
 * @param {string} documentId - Document id.
 * @returns {{doc: object, project: object}|null} Match.
 */
const findDocument = (projects, documentId) => {
  for (const project of projects || []) {
    const doc = (project.generatedDocuments || []).find((d) => d.id === documentId);
    if (doc) return { doc, project };
  }
  return null;
};

/**
 * Find a session and its project.
 * @param {Array<object>} projects - AppContext projects.
 * @param {string} sessionId - Session id.
 * @returns {{session: object, project: object}|null} Match.
 */
const findSession = (projects, sessionId) => {
  for (const project of projects || []) {
    const session = (project.sessions || []).find((s) => s.id === sessionId);
    if (session) return { session, project };
  }
  return null;
};

/**
 * Template id from a template route (`/templates/:id/edit`, `/templates/powerpoint-rich/:id/edit`).
 * @param {string[]} parts - Path segments.
 * @returns {string|null} Template id.
 */
const templateIdFromParts = (parts) => {
  if (parts[0] !== 'templates' || !parts[1]) return null;
  return parts[1] === 'powerpoint-rich' ? parts[2] || null : parts[1];
};

/**
 * Plain-text summary of what the user is looking at, for Inky's system prompt.
 * @param {object} params - Route, auth and app state.
 * @returns {string} Summary.
 */
export function buildContextSummary({ pathname, search, user, account, customers, projects, templates }) {
  const summarizeList = (items) => (items?.length ? items.slice(0, 5).map((item) => item.name).join(', ') : 'None');
  const parts = pathname.split('/');
  let routeContext = '';
  const documentId = parseDocumentEditorRoute(pathname, search);

  if (documentId) {
    const found = findDocument(projects, documentId);
    routeContext = found
      ? `\nActive Document: ${found.doc.name || 'Document'} (id: ${found.doc.id})\nDocument Project: ${found.project.name} (id: ${found.project.id})`
      : `\nActive Document id: ${documentId} (project list may still be loading)`;
  } else if (parts[1] === 'projects' && parts[2]) {
    const project = projects?.find((p) => p.id === parts[2]);
    if (project) routeContext = `\nActive Project: ${project.name} (id: ${project.id})`;
  } else if (parts[1] === 'templates' && parts[2]) {
    const templateId = templateIdFromParts(parts.filter(Boolean));
    const template = templates?.find((t) => t.id === templateId);
    if (template) routeContext = `\nActive Template: ${template.name} (id: ${template.id})`;
  } else if (parts[1] === 'customers' && parts[2] === 'sessions' && parts[3]) {
    const found = findSession(projects, parts[3]);
    if (found) {
      const { session, project } = found;
      const customer = (customers || []).find((c) => c.id === project.customer_id) || project.customer || null;
      routeContext = `\nActive Session: ${session.name} (id: ${session.id})`;
      if (session.description) routeContext += `\nSession Description: ${session.description}`;
      if (session.source) routeContext += `\nSession Source: ${session.source}`;
      routeContext += `\nProject: ${project.name} (id: ${project.id})`;
      if (customer) routeContext += `\nCustomer: ${customer.name} (id: ${customer.id})`;
      if (typeof session.summary === 'string' && session.summary) {
        routeContext += `\nSession Summary Excerpt: ${session.summary.substring(0, 500)}`;
      }
    }
  } else if (parts[1] === 'customers' && parts[2]) {
    const customer = customers?.find((c) => c.id === parts[2]);
    if (customer) routeContext = `\nActive Customer: ${customer.name} (id: ${customer.id})`;
  }

  return [
    `User: ${user?.username || 'User'}`,
    `Route: ${pathname}${routeContext}`,
    `Account: ${account?.name || 'Unknown'}`,
    `Customers (${customers?.length || 0}): ${summarizeList(customers)}`,
    `Projects (${projects?.length || 0}): ${summarizeList(projects)}`,
    `Templates (${templates?.length || 0}): ${summarizeList(templates)}`,
  ].join('\n');
}

/**
 * Entity arrays and active ids for the current route (Inky tools and grounding).
 * @param {object} params - Route + app state.
 * @returns {object} Context data.
 */
export function buildContextData({ pathname, search, customers, projects, templates }) {
  const parts = pathname.split('/');
  let activeSessionId = null;
  let activeSessionProjectId = null;
  let activeProjectId = null;
  let activeDocumentId = null;
  const documentId = parseDocumentEditorRoute(pathname, search);
  if (documentId) {
    activeDocumentId = documentId;
    activeProjectId = findDocument(projects, documentId)?.project.id || new URLSearchParams(search).get('projectId') || null;
  } else if (parts[1] === 'customers' && parts[2] === 'sessions' && parts[3]) {
    activeSessionId = parts[3];
    activeSessionProjectId = findSession(projects, activeSessionId)?.project.id || null;
    activeProjectId = activeSessionProjectId;
  } else if (parts[1] === 'projects' && parts[2]) {
    activeProjectId = parts[2];
  }
  return {
    customers: customers || [],
    projects: projects || [],
    templates: templates || [],
    activeSessionId,
    activeSessionProjectId,
    activeProjectId,
    activeDocumentId,
  };
}

/**
 * One-line "Context: ..." hint under Inky's header.
 * @param {object} params - Route + app state.
 * @returns {string|null} Hint or null when generic.
 */
export function buildContextHint({ pathname, search, customers, projects, templates }) {
  const parts = pathname.split('/').filter(Boolean);
  const documentId = parseDocumentEditorRoute(pathname, search);
  if (documentId) {
    const found = findDocument(projects, documentId);
    return found ? `Context: ${found.doc.name || 'Document'}` : 'Context: Document editor';
  }
  if (parts[0] === 'projects' && parts[1]) {
    const project = projects?.find((p) => p.id === parts[1]);
    return project ? `Context: ${project.name}` : null;
  }
  if (parts[0] === 'customers' && parts[1] === 'sessions' && parts[2]) {
    const found = findSession(projects, parts[2]);
    return found ? `Context: ${found.session.name}` : null;
  }
  if (parts[0] === 'templates' && parts[1]) {
    const template = templates?.find((t) => t.id === templateIdFromParts(parts));
    return template ? `Context: ${template.name}` : null;
  }
  if (parts[0] === 'customers' && parts[1]) {
    const customer = customers?.find((c) => c.id === parts[1]);
    return customer ? `Context: ${customer.name}` : null;
  }
  return null;
}

/**
 * Connects Inky's dock to the route, auth and app data. Kept separate from the shell so data
 * reloads re-render Inky only, never the routed page.
 * @returns {JSX.Element} Dock.
 */
export default function InkyDockContainer() {
  const { pathname, search } = useLocation();
  const { state: authState } = useAuth();
  const { state: appState } = useApp();
  const { customers, projects, templates } = appState;

  const contextSummary = useMemo(
    () => buildContextSummary({ pathname, search, user: authState.user, account: authState.activeAccount, customers, projects, templates }),
    [pathname, search, authState.user, authState.activeAccount, customers, projects, templates]
  );
  const contextData = useMemo(
    () => buildContextData({ pathname, search, customers, projects, templates }),
    [pathname, search, customers, projects, templates]
  );
  const contextHint = useMemo(
    () => buildContextHint({ pathname, search, customers, projects, templates }),
    [pathname, search, customers, projects, templates]
  );

  /** Inky's tool registry (src/assistant/agentTools.js); reads fresh app state at call time. */
  const tools = useAgentTools({ contextData });

  return (
    <ChatAgentDock
      contextSummary={contextSummary}
      tools={tools}
      accountId={authState.activeAccount?.id}
      contextData={contextData}
      contextHint={contextHint}
    />
  );
}
